import { useMemo } from 'react';

import { FeatureFlag } from '@revelio/core';
import {
  type SkillTaxonomySecondarySelectionListQuery,
  TaxonomyType,
} from '@revelio/data-access';

import { setCustomSkillLevelIdMap } from '../../../next/CustomDash/filters/utils/customSkillTaxonomyRegistry';
import {
  buildSkillLevelIdMapFromTaxonomyLevels,
  getCachedCustomSkillNodesFromNested,
  getOrBuildNestedSkillReferenceTree,
  SKILL_REFERENCE_TAXONOMY_TREE_LEVELS,
} from '../../../next/CustomDash/filters/utils/skillReferenceTaxonomyTree';
import {
  buildLevelIdMap,
  type CustomSkillTreeNode,
  getSkillTreeCacheKey,
  skillTreeCache,
} from '../../../next/CustomDash/filters/utils/skillTaxonomyTree';
import { useUnleashFlag } from '../../unleash-flags/use-unleash-flag';
import { useSkillTaxonomy } from '../../user-preferences/hooks/useSkillTaxonomySelection';
import { useCustomSkillSelectionList } from './useCustomSkillSelectionList';
import { useTaxonomyTree } from './useTaxonomyTree';

export type { CustomSkillTreeNode };

type SkillSelectionList =
  | SkillTaxonomySecondarySelectionListQuery['selectionList']
  | undefined;

export const useCustomSkillTaxonomyTree = (
  overrideTaxonomyId?: number,
  taxonomyVersionOverride?: string
) => {
  const useReferenceApi = useUnleashFlag(FeatureFlag.ReferenceApi);
  const prefTaxonomyId = useSkillTaxonomy();
  const skillTaxonomyId = overrideTaxonomyId ?? prefTaxonomyId ?? 0;

  const [
    { data: customSkillSelectionListData, fetching: isLegacySkillListFetching },
  ] = useCustomSkillSelectionList(skillTaxonomyId, {
    pause: useReferenceApi,
  });

  const selectionList =
    customSkillSelectionListData?.selectionList as SkillSelectionList;

  const {
    treeData: rawSkillTaxonomyTreeNodes,
    levels: skillTaxonomyReferenceLevels,
    fetching: isSkillTaxonomyReferenceFetching,
    cacheKey: skillTaxonomyQueryCacheKey,
  } = useTaxonomyTree({
    taxonomyType: TaxonomyType.Skill,
    taxonomyId: skillTaxonomyId,
    taxonomyVersion: taxonomyVersionOverride,
    levels: [...SKILL_REFERENCE_TAXONOMY_TREE_LEVELS],
    pause: !useReferenceApi,
  });

  const cacheKey = useMemo(() => {
    if (useReferenceApi) {
      if (rawSkillTaxonomyTreeNodes.length === 0) return null;
      return `skill-ref:${skillTaxonomyId}:${taxonomyVersionOverride ?? 'latest'}`;
    }
    return getSkillTreeCacheKey(selectionList);
  }, [
    useReferenceApi,
    rawSkillTaxonomyTreeNodes.length,
    skillTaxonomyId,
    taxonomyVersionOverride,
    selectionList,
  ]);

  const levelIdMap = useMemo(() => {
    if (useReferenceApi) {
      if (skillTaxonomyReferenceLevels.length === 0) return null;
      return buildSkillLevelIdMapFromTaxonomyLevels(
        skillTaxonomyReferenceLevels
      );
    }
    if (!selectionList) return null;
    return buildLevelIdMap(selectionList);
  }, [useReferenceApi, skillTaxonomyReferenceLevels, selectionList]);

  const isCustomSkillLevelMapReady =
    levelIdMap !== null && Object.keys(levelIdMap).length > 0;

  if (isCustomSkillLevelMapReady) {
    setCustomSkillLevelIdMap(levelIdMap);
  }

  const skillTaxonomyReferenceNestedTree = useMemo(() => {
    if (!useReferenceApi || rawSkillTaxonomyTreeNodes.length === 0) return null;
    return getOrBuildNestedSkillReferenceTree(
      skillTaxonomyQueryCacheKey,
      rawSkillTaxonomyTreeNodes
    );
  }, [useReferenceApi, rawSkillTaxonomyTreeNodes, skillTaxonomyQueryCacheKey]);

  const customSkillTree = useMemo(() => {
    if (useReferenceApi) {
      if (isSkillTaxonomyReferenceFetching) return null;
      if (!skillTaxonomyReferenceNestedTree?.length) return null;
      const custom = getCachedCustomSkillNodesFromNested(
        skillTaxonomyReferenceNestedTree
      );
      return custom.length > 0 ? custom : null;
    }
    if (!cacheKey || !selectionList) return null;
    return skillTreeCache.get(cacheKey, selectionList);
  }, [
    useReferenceApi,
    isSkillTaxonomyReferenceFetching,
    skillTaxonomyReferenceNestedTree,
    cacheKey,
    selectionList,
  ]);

  const isCustomSkillTaxonomyPending = useReferenceApi
    ? isSkillTaxonomyReferenceFetching
    : isLegacySkillListFetching;

  return {
    customSkillTree,
    isCustomSkillLevelMapReady,
    isCustomSkillTaxonomyPending,
    skillTaxonomyReferenceNestedTree,
    skill_0: customSkillSelectionListData?.selectionList?.skill_0,
    skill_1: customSkillSelectionListData?.selectionList?.skill_1,
    skill_2: customSkillSelectionListData?.selectionList?.skill_2,
    skill_3: customSkillSelectionListData?.selectionList?.skill_3,
    skill_4: customSkillSelectionListData?.selectionList?.skill_4,
  };
};
